import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
  addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO
} from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Video, Ticket, Clock, ExternalLink, Loader2, Copy, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarPage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [icalUrl, setIcalUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');
  const isEl = i18n.language === 'el';
  const dateLocale = isEl ? el : enUS;

  useEffect(() => {
    fetchEvents();
  }, [currentMonth]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(
        `${API}/calendar/events?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`,
        { headers }
      );
      setEvents(res.data.events || []);
      setError(null);
    } catch (e) {
      console.error('Failed to load calendar events:', e);
      const message = e.response?.status === 404
        ? (isEl ? 'Το ημερολόγιο δεν είναι ακόμη διαθέσιμο.' : 'Calendar backend endpoint is not available yet.')
        : (e.response?.data?.detail || (isEl ? 'Σφάλμα φόρτωσης ημερολογίου.' : 'Failed to load calendar events.'));
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchIcalUrl = async () => {
    if (!token) {
      toast.info(isEl ? 'Συνδεθείτε για να λάβετε το iCal link' : 'Login to get iCal link');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/calendar/subscribe`, { headers });
      setIcalUrl(res.data.ical_url);
    } catch (e) {
      toast.error(isEl ? 'Σφάλμα λήψης iCal URL' : 'Failed to get iCal URL');
    }
  };

  const copyIcalUrl = () => {
    if (icalUrl) {
      navigator.clipboard.writeText(icalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(isEl ? 'Αντιγράφηκε!' : 'Copied!');
    }
  };

  const renderHeader = () => (
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl font-bold text-white">
        {format(currentMonth, 'MMMM yyyy', { locale: dateLocale })}
      </h2>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="border-white/20 hover:bg-white/10"
        >
          <ChevronLeft size={20} />
        </Button>
        <Button
          variant="outline"
          onClick={() => setCurrentMonth(new Date())}
          className="border-white/20 hover:bg-white/10"
        >
          {isEl ? 'Σήμερα' : 'Today'}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="border-white/20 hover:bg-white/10"
        >
          <ChevronRight size={20} />
        </Button>
      </div>
    </div>
  );

  const renderDays = () => {
    const days = [];
    const dayNames = isEl 
      ? ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="text-center text-sm font-medium text-gray-400 py-2">
          {dayNames[i]}
        </div>
      );
    }
    return <div className="grid grid-cols-7 border-b border-white/10">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayEvents = events.filter(event => {
          try {
            const eventDate = parseISO(event.start);
            return isSameDay(eventDate, cloneDay);
          } catch {
            return false;
          }
        });

        days.push(
          <div
            key={day.toString()}
            className={`min-h-[100px] p-2 border-r border-b border-white/10 ${
              !isSameMonth(day, monthStart) ? 'bg-white/5 text-gray-600' : ''
            } ${isSameDay(day, new Date()) ? 'bg-primary/10' : ''}`}
          >
            <span className={`text-sm ${isSameDay(day, new Date()) ? 'text-primary font-bold' : 'text-gray-400'}`}>
              {format(day, 'd')}
            </span>
            <div className="mt-1 space-y-1">
              {dayEvents.slice(0, 3).map((event) => (
                <div
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  className={`text-xs p-1 rounded cursor-pointer truncate ${
                    event.has_ticket 
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                      : 'bg-primary/20 text-primary hover:bg-primary/30'
                  }`}
                >
                  {format(parseISO(event.start), 'HH:mm')} {event.title}
                </div>
              ))}
              {dayEvents.length > 3 && (
                <div className="text-xs text-gray-500">
                  +{dayEvents.length - 3} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(<div key={day.toString()} className="grid grid-cols-7">{days}</div>);
      days = [];
    }
    return <div className="border-l border-t border-white/10">{rows}</div>;
  };

  const renderEventModal = () => {
    if (!selectedEvent) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={() => setSelectedEvent(null)}
      >
        <div 
          className="bg-misel-card border border-white/10 rounded-sm max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-xl font-bold text-white mb-3">
            {isEl ? selectedEvent.title_el : selectedEvent.title_en || selectedEvent.title_el}
          </h3>
          
          <div className="space-y-2 text-sm text-gray-400 mb-4">
            <p className="flex items-center gap-2">
              <CalendarIcon size={14} />
              {format(parseISO(selectedEvent.start), 'EEEE, dd MMMM yyyy', { locale: dateLocale })}
            </p>
            <p className="flex items-center gap-2">
              <Clock size={14} />
              {format(parseISO(selectedEvent.start), 'HH:mm')} ({selectedEvent.duration_minutes} min)
            </p>
            {selectedEvent.has_replay && (
              <p className="flex items-center gap-2 text-green-400">
                <Video size={14} />
                {isEl ? 'Διαθέσιμο Replay' : 'Replay Available'}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-bold">
              {selectedEvent.is_free 
                ? (isEl ? 'Δωρεάν' : 'Free')
                : `€${selectedEvent.price}`
              }
            </span>
            {selectedEvent.has_ticket && (
              <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                <Ticket size={12} />
                {isEl ? 'Έχεις εισιτήριο' : 'You have a ticket'}
              </span>
            )}
          </div>

          <div className="flex gap-2">
            {selectedEvent.has_ticket ? (
              <Button
                onClick={() => navigate('/my-seminars')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Ticket size={16} className="mr-2" />
                {isEl ? 'Τα Εισιτήριά μου' : 'My Tickets'}
              </Button>
            ) : (
              <Button
                onClick={() => navigate(`/seminars`)}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                {isEl ? 'Δες Λεπτομέρειες' : 'View Details'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setSelectedEvent(null)}
              className="border-white/20"
            >
              {isEl ? 'Κλείσιμο' : 'Close'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-misel-dark">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <CalendarIcon className="text-primary" size={28} />
            <h1 className="text-3xl font-bold text-white">
              {isEl ? 'Ημερολόγιο Σεμιναρίων' : 'Seminar Calendar'}
            </h1>
          </div>
          
          {/* iCal subscription */}
          <div className="flex items-center gap-2">
            {icalUrl ? (
              <div className="flex items-center gap-2 bg-misel-card border border-white/10 rounded-sm px-3 py-2">
                <input 
                  type="text" 
                  value={icalUrl} 
                  readOnly 
                  className="bg-transparent text-sm text-gray-400 w-48 truncate"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={copyIcalUrl}
                  className="h-8 w-8"
                >
                  {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={fetchIcalUrl}
                className="border-white/20 hover:bg-white/10"
              >
                <ExternalLink size={16} className="mr-2" />
                {isEl ? 'Προσθήκη στο Calendar' : 'Add to Calendar'}
              </Button>
            )}
          </div>
        </div>

        {/* Calendar */}
        {error && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-4 mb-6 text-sm">
            {error}
          </div>
        )}
        <div className="bg-misel-card border border-white/10 rounded-sm p-4">
          {renderHeader()}
          {renderDays()}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-primary" size={32} />
            </div>
          ) : (
            renderCells()
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary/20"></div>
            <span className="text-gray-400">{isEl ? 'Διαθέσιμο σεμινάριο' : 'Available seminar'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500/20"></div>
            <span className="text-gray-400">{isEl ? 'Έχεις εισιτήριο' : 'You have ticket'}</span>
          </div>
        </div>
      </div>

      {renderEventModal()}
    </div>
  );
}
